import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const TIERS = {
  LITE: 'lite',
  WEALTH: 'wealth',
  ELITE: 'elite',
};

export const TIER_INFO = {
  [TIERS.LITE]: {
    name: 'Lite',
    color: '#14B8A6',
    price: 'Free',
  },
  [TIERS.WEALTH]: {
    name: 'Wealth',
    color: '#3b82f6',
    price: '₹2,999/year',
  },
  [TIERS.ELITE]: {
    name: 'Elite',
    color: '#d4af37',
    price: '₹14,999/year',
  },
};

// Features and their required tiers
export const FEATURE_TIERS = {
  // Lite features (available to all)
  'allocation-sandbox': TIERS.LITE,
  'category-visualizations': TIERS.LITE,
  'drawdown-analysis': TIERS.LITE,
  'what-if-scenarios': TIERS.LITE,
  'basic-optimization': TIERS.LITE,

  // Wealth features
  'sebi-advisor-sessions': TIERS.WEALTH,
  'goal-based-plans': TIERS.WEALTH,
  'ai-pre-analysis': TIERS.WEALTH,
  'pdf-reports': TIERS.WEALTH,
  'priority-email-support': TIERS.WEALTH,

  // Elite features
  'continuous-monitoring': TIERS.ELITE,
  'multi-asset-view': TIERS.ELITE,
  'priority-advisor-call': TIERS.ELITE,
  'family-portfolio': TIERS.ELITE,
  'stress-testing': TIERS.ELITE,
  'custom-constraints': TIERS.ELITE,
  'white-label-reports': TIERS.ELITE,
};

const TIER_HIERARCHY = {
  [TIERS.LITE]: 0,
  [TIERS.WEALTH]: 1,
  [TIERS.ELITE]: 2,
};

export const ThemeProvider = ({ children }) => {
  const [currentTier, setCurrentTier] = useState(() => {
    // Load from localStorage or default to 'lite'
    const saved = localStorage.getItem('allocq-tier');
    return saved && Object.values(TIERS).includes(saved) ? saved : TIERS.LITE;
  });

  const [showPricing, setShowPricing] = useState(false);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTier);
    localStorage.setItem('allocq-tier', currentTier);
  }, [currentTier]);

  // Check if a feature is available for current tier
  const hasFeature = (featureKey) => {
    const requiredTier = FEATURE_TIERS[featureKey];
    if (!requiredTier) return true; // Unknown features default to available

    return TIER_HIERARCHY[currentTier] >= TIER_HIERARCHY[requiredTier];
  };

  // Get required tier for a feature
  const getRequiredTier = (featureKey) => {
    return FEATURE_TIERS[featureKey] || TIERS.LITE;
  };

  // Check if user can upgrade
  const canUpgrade = () => {
    return currentTier !== TIERS.ELITE;
  };

  // Get next tier
  const getNextTier = () => {
    if (currentTier === TIERS.LITE) return TIERS.WEALTH;
    if (currentTier === TIERS.WEALTH) return TIERS.ELITE;
    return null;
  };

  // Upgrade/change tier
  const changeTier = (newTier) => {
    if (Object.values(TIERS).includes(newTier)) {
      setCurrentTier(newTier);
    }
  };

  const value = {
    currentTier,
    tierInfo: TIER_INFO[currentTier],
    hasFeature,
    getRequiredTier,
    canUpgrade,
    getNextTier,
    changeTier,
    showPricing,
    setShowPricing,
    TIERS,
    TIER_INFO,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
